// Preenchimento do formulário pós-ligação no encerramento (wrap-up) de uma
// chamada. Renderiza a definição PUBLICADA da org (GET /v1/call-form) num diálogo
// e grava as respostas (POST /v1/calls/:id/form). Complementa a tabulação — não a
// substitui. Aparece só quando há formulário publicado (anti-botão-falso).
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./i18n.js";
import { toast } from "sonner";
import { ClipboardList } from "lucide-react";
import type { CallFormDef, FormField } from "@/api/call-form";
import { useActiveCallForm, useSubmitCallForm } from "@/hooks/use-call-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Um campo tem valor "presente"? (para checar obrigatórios no cliente).
// O Select do Radix não aceita item com value vazio; a sentinela é o item
// "Selecione" e volta a ser `undefined` na resposta do formulário.
const NO_ANSWER = "__none__";

function isPresent(v: unknown): boolean {
  if (v === undefined || v === null) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

function activeSorted(form: CallFormDef): FormField[] {
  return form.fields.filter((f) => f.active).sort((a, b) => a.order - b.order);
}

// Renderiza o input de um campo conforme o tipo.
function FieldInput({
  field,
  value,
  onChange,
}: {
  field: FormField;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const { t } = useTranslation();
  switch (field.type) {
    case "radio":
      return (
        <RadioGroup value={typeof value === "string" ? value : ""} onValueChange={onChange}>
          {(field.options ?? []).map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm">
              <RadioGroupItem value={o.value} id={`${field.id}-${o.value}`} />
              {o.label}
            </label>
          ))}
        </RadioGroup>
      );
    case "select":
      return (
        <Select
          value={typeof value === "string" && value ? value : NO_ANSWER}
          onValueChange={(v) => onChange(v === NO_ANSWER ? undefined : v)}
        >
          <SelectTrigger className="w-full" aria-label={field.label}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {/* O item "Selecione" continua existindo (e não vira só placeholder)
                porque é como se LIMPA a resposta de um campo opcional. */}
            <SelectItem value={NO_ANSWER}>{t("callForms.select")}</SelectItem>
            {(field.options ?? []).map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case "checkbox": {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="flex flex-col gap-1.5">
          {(field.options ?? []).map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={arr.includes(o.value)}
                onCheckedChange={(c) =>
                  onChange(c ? [...arr, o.value] : arr.filter((v) => v !== o.value))
                }
              />
              {o.label}
            </label>
          ))}
        </div>
      );
    }
    case "rating": {
      const min = field.min ?? 0;
      const max = field.max ?? 5;
      const scale = Array.from({ length: max - min + 1 }, (_, i) => min + i);
      return (
        <div className="flex flex-wrap gap-1.5">
          {scale.map((n) => (
            <Button
              key={n}
              type="button"
              variant={value === n ? "default" : "outline"}
              size="sm"
              className="w-10"
              onClick={() => onChange(n)}
            >
              {n}
            </Button>
          ))}
        </div>
      );
    }
    case "number":
      return (
        <Input
          type="number"
          value={typeof value === "number" ? value : ""}
          min={field.min}
          max={field.max}
          onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
        />
      );
    case "textarea":
      return (
        <Textarea
          value={typeof value === "string" ? value : ""}
          maxLength={field.maxLength}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    default: // text
      return (
        <Input
          value={typeof value === "string" ? value : ""}
          maxLength={field.maxLength}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

export function CallFormPanel({
  callId,
  form,
  onSaved,
  onBack,
  submitLabel,
  backLabel,
}: {
  callId: string;
  form: CallFormDef;
  onSaved: () => void;
  onBack?: () => void;
  submitLabel?: string;
  backLabel?: string;
}) {
  const { t } = useTranslation();
  const effectiveSubmitLabel = submitLabel ?? t("callForms.save");
  const effectiveBackLabel = backLabel ?? t("callForms.back");
  const fields = useMemo(() => activeSorted(form), [form]);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});

  const mutation = useSubmitCallForm({
    onSuccess: () => {
      toast.success(t("callForms.submitted"));
      onSaved();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function submit() {
    // Obrigatórios ausentes — barra antes de bater no back (que também valida).
    const missing = fields.find((f) => f.required && !isPresent(answers[f.id]));
    if (missing) {
      toast.error(t("callForms.fillRequired", { label: missing.label }));
      return;
    }
    mutation.mutate({ callId, answers });
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        {fields.map((field) => (
          <div key={field.id} className="flex flex-col gap-1.5">
            <Label>
              {field.label}
              {field.required && <span className="ml-1 text-destructive">*</span>}
            </Label>
            <FieldInput
              field={field}
              value={answers[field.id]}
              onChange={(v) => setAnswers((a) => ({ ...a, [field.id]: v }))}
            />
          </div>
        ))}
      </div>

      <DialogFooter>
        {onBack && (
          <Button variant="outline" onClick={onBack} disabled={mutation.isPending}>
            {effectiveBackLabel}
          </Button>
        )}
        <Button onClick={submit} disabled={mutation.isPending}>
          {mutation.isPending && <Spinner className="size-4" />}
          {effectiveSubmitLabel}
        </Button>
      </DialogFooter>
    </>
  );
}

function FormDialog({
  callId,
  form,
  open,
  onOpenChange,
}: {
  callId: string;
  form: CallFormDef;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("callForms.title")}</DialogTitle>
          <DialogDescription>{t("callForms.submitDescription")}</DialogDescription>
        </DialogHeader>

        <CallFormPanel
          callId={callId}
          form={form}
          onSaved={() => onOpenChange(false)}
          onBack={() => onOpenChange(false)}
          backLabel={t("callForms.cancel")}
          submitLabel={t("callForms.send")}
        />
      </DialogContent>
    </Dialog>
  );
}

// Botão que abre o formulário pós-ligação de uma chamada. Só aparece quando a org
// tem um formulário PUBLICADO (com ao menos um campo ativo) — do contrário, nada.
export function CallFormButton({ callId }: { callId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data } = useActiveCallForm();
  const form = data?.form;
  const hasForm = !!form?.active && activeSorted(form).length > 0;
  if (!hasForm) return null;

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <ClipboardList className="size-4" />
        {t("callForms.button")}
      </Button>
      {open && <FormDialog callId={callId} form={form} open={open} onOpenChange={setOpen} />}
    </>
  );
}
